/**
 * screens/Onboarding
 * ──────────────────────────────────────────────────────────────────────────
 * Two settings and the console opens.
 *
 * This REPLACES the entire finance console until a base currency and an
 * approval threshold are both set — the `console` gate. Not a wizard, not a
 * modal over an empty dashboard: until those two values exist, no figure the
 * console could show would mean anything, because there is no currency to
 * denominate it in and no threshold to judge a spend against.
 *
 * WHY ONLY TWO QUESTIONS. Spend categories, variance routing, the connectors
 * and the offboarding feed are all required eventually, and every one of them
 * is asked for at the moment it is first needed instead. The "Asked later, in
 * context" block lists them with WHEN each will be asked, so nobody has to
 * wonder what else is coming. Front-loading six questions is how a setup
 * screen gets abandoned.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  Button,
  Card,
  Chip,
  Eyebrow,
  Input,
  PageContainer,
  StatusPill,
} from '../../components';
import { neutral } from '../../theme/colors';
import { mono, numeric, sans, size as fontSize, leading, type as textRole } from '../../theme/typography';
import { formatMinor, parseMajorToMinor } from '../../lib/money';
import { patchPolicy } from '../../services/config/config.service';
import { handleApiError } from '../../utils/errorHandler';
import { showCompleted, showValidation } from '../../utils/successHandler';
import { useConfig } from '../../context/ConfigContext';

/**
 * The currencies offered before the org has an option list of its own — the
 * three the design names. The API seeds its full default list (which also
 * includes USD) the moment the policy is saved.
 */
const STARTER_CURRENCIES = ['AED', 'INR', 'SAR'] as const;

/**
 * The four settings deferred to point of use, with when each is asked. Copy is
 * from the design source.
 */
const ASKED_LATER = [
  { key: 'categories', label: 'Spend categories', when: 'Before the first spend' },
  { key: 'variance', label: 'Variance routing', when: 'Before the first cash count' },
  { key: 'connectors', label: 'Payroll and Billing', when: 'Before anything syncs out' },
  { key: 'offboard', label: 'Offboarding feed', when: 'When someone leaves' },
] as const;

export function OnboardingScreen() {
  const { policy, optionsByKind, reload } = useConfig();

  const [currency, setCurrency] = useState<string | null>(policy?.base_currency ?? null);
  const [thresholdText, setThresholdText] = useState('');
  const [saving, setSaving] = useState(false);

  /**
   * The currencies this screen offers.
   *
   * THE CHICKEN AND EGG THIS SOLVES — and it was a hard dead end:
   * sk-company-cash-api seeds the option lists on the FIRST POLICY SAVE
   * (`seedDefaultOptions` runs inside `patchPolicy`). So on a workspace that
   * has never been configured, `GET /v1/config/bootstrap` returns
   * `options: []` — and reading the currency chips from that list rendered NO
   * chips at all. With no currency there was nothing to pick, the threshold
   * field stayed disabled, "Open the console" never enabled, and onboarding
   * could not be completed. Ever.
   *
   * So this falls back to the starter set the design names (AED / INR / SAR)
   * whenever the org has no list yet, and uses the org's own list once one
   * exists — because an org that has since added a currency should see it here.
   * Saving the policy seeds the full default list server-side.
   */
  const currencyOptions = useMemo(() => {
    const own = optionsByKind.currency.filter((option) => option.is_active);
    if (own.length > 0) return own.map((option) => option.value);
    return STARTER_CURRENCIES;
  }, [optionsByKind]);

  const currencySet = !!policy?.base_currency;
  const thresholdSet = policy?.threshold_minor !== null && policy?.threshold_minor !== undefined;
  const remaining = (currencySet ? 0 : 1) + (thresholdSet ? 0 : 1);

  /** Save whichever of the two the user has just completed. */
  const save = async () => {
    // The currency must be chosen before the threshold can be interpreted: an
    // amount with no currency has no scale, so "1000" could be 10.00 or 1000.
    if (!currency) {
      showValidation('Choose a base currency first.');
      return;
    }

    const minor = parseMajorToMinor(thresholdText, currency);
    if (thresholdText.trim() && minor === null) {
      showValidation('That is not a valid amount for this currency.');
      return;
    }

    setSaving(true);
    try {
      const result = await patchPolicy({
        base_currency: currency,
        ...(minor ? { threshold_minor: minor } : {}),
      });
      const seeded = result.seeded_rules?.length ?? 0;
      showCompleted(
        result,
        'Setup saved',
        seeded > 0
          ? `The console is open. ${seeded} approval rule${seeded === 1 ? '' : 's'} created from your threshold.`
          : 'The console is open.',
      );
      await reload();
    } catch (error) {
      handleApiError(error, 'Could not save your setup');
    } finally {
      setSaving(false);
    }
  };

  const canOpen = !!currency && (thresholdSet || parseMajorToMinor(thresholdText, currency ?? 'AED') !== null);

  return (
    <PageContainer width="onboarding" onboarding testID="screen-onboarding">
      <Eyebrow style={styles.eyebrow}>{`Setup · ${remaining} of 2 left`}</Eyebrow>
      <Text style={[textRole.onboardingTitle, styles.title]}>
        Two settings and the console opens
      </Text>
      <Text style={styles.blurb}>Everything else is asked for when you first need it.</Text>

      <View style={styles.steps}>
        {/* ── 1 · Base currency ──────────────────────────────────────── */}
        <Card padded roomy>
          <View style={styles.step}>
            <Text style={styles.stepNumber}>1</Text>
            <View style={styles.stepBody}>
              <Text style={styles.stepLabel}>Base currency</Text>
              <Text style={styles.stepHint}>
                Thresholds and charts are denominated in this. Balances in other currencies are
                never converted into it.
              </Text>

              {currencySet ? (
                <Text style={styles.stepValue}>{policy?.base_currency}</Text>
              ) : (
                <View style={styles.options}>
                  {currencyOptions.map((code) => (
                    <Chip
                      key={code}
                      label={code}
                      selected={currency === code}
                      onPress={() => setCurrency(code)}
                    />
                  ))}
                </View>
              )}
            </View>
            {currencySet && <StatusPill label="Set" tone="positive" capitalize={false} />}
          </View>
        </Card>

        {/* ── 2 · Approval threshold ─────────────────────────────────── */}
        <Card padded roomy>
          <View style={styles.step}>
            <Text style={styles.stepNumber}>2</Text>
            <View style={styles.stepBody}>
              <Text style={styles.stepLabel}>Approval threshold</Text>
              <Text style={styles.stepHint}>
                Spends at or above this wait for an approver. Saving it creates your first approval
                rule, which you can edit in Settings.
              </Text>

              {thresholdSet && policy?.base_currency ? (
                <Text style={styles.stepValue}>
                  {policy.base_currency} {formatMinor(policy.threshold_minor, policy.base_currency)}
                </Text>
              ) : (
                <View style={styles.thresholdField}>
                  <Input
                    value={thresholdText}
                    onChangeText={setThresholdText}
                    placeholder="0.00"
                    prefix={currency ?? undefined}
                    numeric
                    width={220}
                    // Without a currency there is no decimal scale to validate
                    // against, so the field waits for step 1.
                    disabled={!currency}
                  />
                </View>
              )}
            </View>
            {thresholdSet && <StatusPill label="Set" tone="positive" capitalize={false} />}
          </View>
        </Card>
      </View>

      {/* ── Asked later, in context ──────────────────────────────────── */}
      <View style={styles.later}>
        <Eyebrow style={styles.laterTitle}>Asked later, in context</Eyebrow>
        <View style={styles.laterList}>
          {ASKED_LATER.map((item) => (
            <View key={item.key} style={styles.laterRow}>
              <View style={styles.laterText}>
                <Text style={styles.laterLabel}>{item.label}</Text>
                <Text style={styles.laterWhen}>{item.when}</Text>
              </View>
              <Text style={styles.laterWhere}>Settings</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.footer}>
        <Button
          label="Open the console"
          size="xl"
          variant="primary"
          disabled={!canOpen}
          loading={saving}
          onPress={save}
        />
      </View>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  eyebrow: { fontSize: fontSize.tableHeader, marginBottom: 10 },
  title: { marginBottom: 6 },
  blurb: {
    ...sans('400'),
    fontSize: fontSize.body,
    lineHeight: leading(fontSize.body),
    color: neutral.grey600,
    maxWidth: 520,
    marginBottom: 26,
  },
  steps: { gap: 10, marginBottom: 26 },
  step: { flexDirection: 'row', alignItems: 'flex-start', gap: 13 },
  stepNumber: {
    ...mono('400'),
    fontSize: fontSize.metaSm,
    color: neutral.grey600,
    width: 14,
    paddingTop: 2,
  },
  stepBody: { flex: 1, minWidth: 0 },
  stepLabel: { ...sans('600'), fontSize: fontSize.sm, color: neutral.ink, marginBottom: 2 },
  stepHint: {
    ...sans('400'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.grey600,
  },
  stepValue: { ...mono('500'), fontSize: fontSize.meta, ...numeric, color: neutral.ink, marginTop: 10 },
  options: { flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginTop: 13 },
  thresholdField: { marginTop: 13 },

  later: { borderTopWidth: 1, borderTopColor: neutral.border, paddingTop: 20, marginBottom: 26 },
  laterTitle: { fontSize: fontSize.tableHeader, marginBottom: 12 },
  laterList: { gap: 9 },
  laterRow: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 },
  laterText: { flex: 1, minWidth: 0 },
  laterLabel: { ...sans('400'), fontSize: fontSize.button, color: neutral.ink2 },
  laterWhen: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey400 },
  laterWhere: { ...mono('400'), fontSize: fontSize.tableHeader, color: neutral.grey400, flexShrink: 0 },

  footer: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
});

export default OnboardingScreen;
